import React, { useState } from 'react';
import { styled } from '@mui/system';
import Slider from '@mui/material/Slider';
import { Card, Body, PrimaryButton, ExplainBox, FxLine } from '../components/ui';
import { motion, fadeUp, popIn } from '../components/motion';
import { scoreAllocate } from '../engine/scoring';
import { L, t } from '../strings';

const COLORS = ['#79c912', '#30b0c7', '#f7d53e', '#9c27b0', '#19857b', '#e25510'];

const Row = styled('div')(({ delay }) => ({
  marginBottom: 14,
  ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`)
}));

const Dot = styled('span')(({ color }) => ({
  display: 'inline-block',
  width: 10,
  height: 10,
  borderRadius: 3,
  backgroundColor: color,
  marginRight: 8
}));

const StackLabel = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  color: theme.palette.textTertiary.main,
  marginBottom: 6
}));

const StackBar = styled('div')(({ theme }) => ({
  display: 'flex',
  height: 18,
  borderRadius: 100,
  overflow: 'hidden',
  marginBottom: 18,
  backgroundColor: theme.palette.grey[50],
  backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.04) 0 6px, transparent 6px 12px)',
  ...motion(`${fadeUp} 360ms ease-out both`),
  '& > span': {
    height: '100%',
    transition: 'width 300ms cubic-bezier(.2,.8,.2,1)',
    boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.6)'
  }
}));

const Left = styled('span')(({ zero }) => ({
  fontWeight: 500,
  color: zero ? '#4f9a04' : '#222',
  display: 'inline-block',
  ...motion(`${popIn} 260ms ease-out both`)
}));
const LabelRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontFamily: 'Rubik-Regular',
  fontSize: 16,
  fontWeight: 500
}));
const Hint = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.textTertiary.main,
  fontFamily: 'Rubik-Regular'
}));
const Best = styled('span')(() => ({
  fontSize: 13,
  color: '#4f9a04',
  fontWeight: 500,
  marginLeft: 8
}));

export default function AllocateStep({ step, answered, onSubmit, kpiLabels }) {
  const [alloc, setAlloc] = useState(() => Object.fromEntries(step.buckets.map((b) => [b.id, 0])));
  const total = Object.values(alloc).reduce((a, b) => a + b, 0);
  const left = 100 - total;
  const done = !!answered;

  const set = (id, v) => {
    if (done) return;
    const others = total - alloc[id];
    setAlloc({ ...alloc, [id]: Math.min(v, 100 - others) });
  };

  const submit = () => {
    const score = scoreAllocate(alloc, step.buckets);
    onSubmit({ score, fx: score >= 0.75 ? step.fx?.ok : step.fx?.fail, alloc });
  };

  const shown = done ? answered.alloc : alloc;
  const stack = (values) => (
    <StackBar>
      {step.buckets.map((b, i) => (
        <span
          key={b.id}
          title={`${L(b.label)}: ${values[b.id]}%`}
          style={{ width: `${values[b.id]}%`, backgroundColor: COLORS[i % COLORS.length] }}
        />
      ))}
    </StackBar>
  );

  return (
    <Card>
      <Body style={{ marginBottom: 16 }}>{L(step.text)}</Body>
      <StackLabel>
        <span>{done ? t('yourAnswer') : '100%'}</span>
        {!done && (
          <Left key={left} zero={left === 0}>
            {t('remaining', { n: left })}
          </Left>
        )}
      </StackLabel>
      {stack(shown)}
      {done && (
        <>
          <StackLabel>
            <span>{t('recommended')}</span>
          </StackLabel>
          {stack(Object.fromEntries(step.buckets.map((b) => [b.id, b.best])))}
        </>
      )}
      {step.buckets.map((b, i) => (
        <Row key={b.id} delay={80 + i * 60}>
          <LabelRow>
            <span>
              <Dot color={COLORS[i % COLORS.length]} />
              {L(b.label)}
              {done && (
                <Best>
                  {t('recommended')}: {b.best}%
                </Best>
              )}
            </span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{shown[b.id]}%</span>
          </LabelRow>
          <Hint>{L(b.hint)}</Hint>
          <Slider
            value={shown[b.id]}
            onChange={(_, v) => set(b.id, v)}
            step={5}
            min={0}
            max={100}
            disabled={done}
            marks={done ? [{ value: b.best }] : undefined}
            sx={{
              color: COLORS[i % COLORS.length],
              marginTop: '2px',
              '& .MuiSlider-mark': {
                width: 4,
                height: 14,
                borderRadius: 2,
                backgroundColor: '#222'
              },
              '&.Mui-disabled': { color: COLORS[i % COLORS.length], opacity: 0.85 }
            }}
          />
        </Row>
      ))}
      {!done ? (
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <PrimaryButton disabled={left !== 0} onClick={submit}>
            {t('submit')}
          </PrimaryButton>
        </div>
      ) : (
        <ExplainBox
          ok={answered.score >= 0.75}
          label={`${t('score')}: ${Math.round(answered.score * 100)}/100`}>
          {L(step.explain)}
          <FxLine fx={answered.fx} kpiLabels={kpiLabels} />
        </ExplainBox>
      )}
    </Card>
  );
}
